<template>
  <div class="min-h-screen bg-slate-50 dark:bg-slate-900" :dir="dir">
    <Navbar />

    <section class="container py-20 md:py-28">
      <div class="flex justify-center">
        <div class="w-full max-w-lg">
          <div
            class="p-6 bg-white border shadow-sm rounded-2xl border-slate-200 dark:border-slate-700 dark:bg-slate-800 md:p-8"
          >
            <h1 class="mb-1 text-xl font-bold text-center text-slate-900 dark:text-white">
              {{ t("verify_account") }}
            </h1>
            <p class="mb-2 text-sm text-center text-slate-500 dark:text-slate-400">
              {{ t("otp_sent_to") }}
              <span
                class="font-semibold text-slate-700 dark:text-slate-200"
                dir="ltr"
              >
                {{ destination }}
              </span>
            </p>
            <p
              v-if="verifyStore.otpExpiryTimer > 0"
              class="mb-6 text-xs text-center text-slate-500 dark:text-slate-400"
            >
              {{ t("verify_code_expires_in") }}
              <span class="font-semibold tabular-nums text-primary">
                {{ formatCountdown(verifyStore.otpExpiryTimer) }}
              </span>
            </p>
            <p v-else-if="verifyStore.otpExpired" class="mb-6 text-xs text-center text-red-500">
              {{ t("verify_code_expired") }}
            </p>
            <div v-else class="mb-6"></div>

            <form @submit.prevent="verify" class="grid grid-cols-1 gap-4">
              <div>
                <label class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  {{ t("fp_otp_label") }}
                </label>

                <div class="flex justify-center gap-2" dir="ltr">
                  <input
                    v-for="(d, i) in otp"
                    :key="i"
                    type="text"
                    inputmode="numeric"
                    maxlength="1"
                    autocomplete="one-time-code"
                    class="w-12 h-12 text-center bg-white border rounded-lg focus:outline-none focus:ring-2 border-primary/30 focus:ring-primary/40 dark:bg-slate-900 text-slate-900 dark:text-white"
                    dir="ltr"
                    style="unicode-bidi: plaintext"
                    v-model="otp[i]"
                    @input="onOtpInput(i, $event)"
                    @keydown.backspace.prevent="onOtpBackspace(i, $event)"
                    @paste.prevent="onOtpPaste($event)"
                    :ref="(el) => (otpRefs[i] = el)"
                  />
                </div>

                <div class="flex items-center justify-center gap-3 mt-3">
                  <button
                    type="button"
                    class="text-xs font-semibold transition text-primary hover:text-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="!verifyStore.canResend"
                    @click="resend"
                  >
                    <span v-if="verifyStore.resendTimer > 0">
                      {{ t("fp_resend_in") }}
                      <span class="tabular-nums">{{ verifyStore.resendTimer }}</span
                      >{{ t("fp_seconds") }}
                    </span>
                    <span v-else-if="verifyStore.resendLoading">
                      <i class="mdi mdi-loading mdi-spin"></i>
                      {{ t("fp_sending") }}
                    </span>
                    <span v-else>{{ t("fp_resend") }}</span>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                class="inline-flex items-center justify-center w-full h-10 px-6 text-sm font-semibold text-black rounded-md bg-secondary hover:bg-secondary/90 disabled:opacity-60"
                :disabled="verifyStore.loading || verifyStore.otpExpired"
              >
                <span v-if="!verifyStore.loading">{{ t("fp_continue") }}</span>
                <span v-else class="inline-flex items-center gap-2">
                  <i class="text-xl mdi mdi-loading mdi-spin"></i>
                  {{ t("fp_verifying") }}
                </span>
              </button>

              <p class="text-sm text-center text-slate-600 dark:text-slate-300">
                <template v-if="verifySource === 'login'">
                  <RouterLink
                    to="/login"
                    class="font-semibold text-primary hover:text-primary/90"
                    @click="onLeaveVerify"
                  >
                    {{ t("verify_back_to_login") }}
                  </RouterLink>
                </template>
                <template v-else>
                  {{ t("verify_wrong_email") }}
                  <RouterLink
                    to="/registerClient"
                    class="font-semibold text-primary hover:text-primary/90"
                    @click="onLeaveVerify"
                  >
                    {{ t("verify_back_to_register") }}
                  </RouterLink>
                </template>
              </p>
            </form>
          </div>
        </div>
      </div>

      <Toast
        :show="toast.show"
        :type="toast.type"
        :message="toast.message"
        @update:show="toast.show = $event"
      />
    </section>
  </div>
  <Footer />
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import Navbar from "@/components/Navbar.vue";
import Footer from "@/components/Footer.vue";
import Toast from "@/components/Toast.vue";
import { messages } from "@/i18n";
import { useVerifyAccountStore } from "@/stores/verifyAccountStore";
import {
  clearPendingVerification,
  getVerifyDestination,
  getVerifySource,
  hasPendingVerification,
  isVerifyTokenExpired,
} from "@/utils/pendingVerification";

const router = useRouter();
const verifyStore = useVerifyAccountStore();

const lang = ref(
  (document.documentElement.lang || localStorage.getItem("app_lang") || "ar")
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar"
);

function t(key) {
  return (messages[lang.value] && messages[lang.value][key]) || key;
}

const dir = computed(() => (lang.value === "ar" ? "rtl" : "ltr"));
const destination = computed(() => getVerifyDestination());
const verifySource = computed(() => getVerifySource());

let _langObserver = null;

onMounted(async () => {
  if (!hasPendingVerification() || isVerifyTokenExpired()) {
    clearPendingVerification();
    router.replace({ name: "Login" });
    return;
  }

  verifyStore.bootstrapTimers();

  _langObserver = new MutationObserver(() => {
    const htmlLang = document.documentElement.lang || "ar";
    lang.value = htmlLang.toLowerCase().startsWith("en") ? "en" : "ar";
  });

  _langObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["lang"],
  });

  await nextTick();
  otpRefs.value?.[0]?.focus?.();
});

onBeforeUnmount(() => {
  if (_langObserver) _langObserver.disconnect();
});

const otp = ref(["", "", "", "", "", ""]);
const otpRefs = ref([]);

const toast = reactive({
  show: false,
  type: "error",
  message: "",
});

function showToast(message, type = "error") {
  toast.message = message;
  toast.type = type;
  toast.show = true;
}

function formatCountdown(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function validateOtpOnly() {
  if (otp.value.join("").length !== 6) {
    showToast(t("fp_err_otp_invalid") || t("otp_invalid"), "error");
    return false;
  }
  return true;
}

function handleSessionExpired() {
  clearPendingVerification();
  verifyStore.reset();
  showToast(t("verify_session_expired_login"), "error");
  router.replace({ name: "Login" });
}

async function verify() {
  if (verifyStore.loading || verifyStore.otpExpired) return;
  if (!validateOtpOnly()) return;

  try {
    await verifyStore.verifyOtp(otp.value.join(""));

    verifyStore.reset();
    showToast(t("verified_success") || t("verify_account"), "success");
    router.replace({ name: "UserProfile" });
  } catch (e) {
    const status = e?.response?.status;

    if (status === 401) {
      handleSessionExpired();
      return;
    }

    const message =
      verifyStore.errors?.message ||
      e?.response?.data?.message ||
      t("otp_invalid") ||
      "Invalid code.";
    showToast(message, "error");
  }
}

async function resend() {
  try {
    await verifyStore.resendCode();
    showToast(t("fp_toast_code_resent") || t("resend"), "success");
  } catch (e) {
    const status = e?.response?.status;
    const message =
      verifyStore.errors?.message ||
      e?.response?.data?.message ||
      t("fp_cooldown_message");

    if (status === 401) {
      handleSessionExpired();
      return;
    }

    showToast(message, "error");
  }
}

function onLeaveVerify() {
  clearPendingVerification();
  verifyStore.reset();
}

function onOtpInput(i, e) {
  const val = String(e?.target?.value ?? "").replace(/\D/g, "");
  otp.value[i] = val.slice(-1);
  if (val && otpRefs.value[i + 1]) otpRefs.value[i + 1]?.focus?.();

  if (otp.value.join("").length === 6) {
    verify();
  }
}

function onOtpBackspace(i) {
  if (!otp.value[i] && otpRefs.value[i - 1]) otpRefs.value[i - 1]?.focus?.();
  else otp.value[i] = "";
}

function onOtpPaste(e) {
  const paste = (e.clipboardData || window.clipboardData)
    ?.getData("text")
    ?.replace(/\D/g, "");
  if (!paste) return;

  const next = [...otp.value];
  for (let i = 0; i < Math.min(6, paste.length); i++) next[i] = paste[i];
  otp.value = next;

  nextTick(() => {
    otpRefs.value?.[Math.min(paste.length, 5)]?.focus?.();
    if (next.join("").length === 6) verify();
  });
}
</script>
